import { z } from 'zod';
import { ComponentType } from '$lib/sanity/types';
import { ModalSchema } from './Modal';
import { SanityDocumentSchema, SanityBlockSchema } from '../sanity';
import { NewsPostSchema } from '../entities/NewsPost';
import { DocumentSchema } from '../entities/Document';
import { PageTreeHierarchyItemSchema } from '../entities/PageTree';
import { PageSchema } from '../entities/Page';
import { LegalPageSchema } from '../entities/LegalPage';
import { NewsTopicPageSchema } from '../entities/NewsTopicPage';
import { GithubButtonSchema } from './GithubButtons';
import { ImageAssetSchema } from './Image';
import { ShapeLinkGroupLinkSchema } from './ShapeLinkGroup';

export const LinkPageSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.lazy(() => NewsPostSchema),
    z.lazy(() => LegalPageSchema),
    z.lazy(() => DocumentSchema),
    z.lazy(() =>
      PageSchema.extend({
        hierarchyItems: z
          .array(PageTreeHierarchyItemSchema)
          .nullable()
          .transform((x) => x ?? [])
      })
    ),
    z.lazy(() => NewsTopicPageSchema)
  ])
);

export const TextBlockSchema = SanityBlockSchema.extend({
  image: ImageAssetSchema.nullish(),
  links: z.array(ShapeLinkGroupLinkSchema).nullish(),
  starButton: GithubButtonSchema.nullish(),
  forkButton: GithubButtonSchema.nullish(),
  followButton: GithubButtonSchema.nullish(),
  markDefs: z
    .array(
      z.object({
        _key: z.string(),
        _type: z.string(),
        href: z.string().optional().nullable(),
        modal: ModalSchema.optional().nullable(),
        page: LinkPageSchema.nullish(),
        type: z.string().optional().nullable(),
        url: z.string().optional().nullable(),
        anchorId: z.string().optional().nullable(),
        addToAnchorNavigationBar: z
          .boolean()
          .nullish()
          .transform((x) => (x ? x : false)),
        anchorNavigationBarText: z.string().optional().nullable(),
        declineCookie: z.boolean().optional().nullable(),
        anchorKey: z.string().optional().nullable()
      })
    )
    .optional()
    .nullable()
});

export const TextDividerSchema = SanityDocumentSchema.extend({
  _type: z.literal(ComponentType.TextDivider)
});

export const TextSchema = SanityDocumentSchema.extend({
  _type: z.literal(ComponentType.Text),
  body: z.lazy(() => z.array(TextBlockSchema).optional())
});

export const AnchorMarkSchema = z.object({
  _type: z.literal('anchor'),
  _key: z.string(),
  anchorId: z.string().optional().nullable(),
  addToAnchorNavigationBar: z
    .boolean()
    .optional()
    .transform((x) => (x ? x : false)),
  anchorNavigationBarText: z.string().optional().nullable()
});

export type LinkPage = z.infer<typeof LinkPageSchema>;
export type AnchorMark = z.infer<typeof AnchorMarkSchema>;
export type Text = z.infer<typeof TextSchema>;
export type TextBlock = z.infer<typeof TextBlockSchema>;
export type TextDivider = z.infer<typeof TextDividerSchema>;
